import type { Company } from '@/scenes/authentication/types/types.auth';
import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '@shared/services/api';
import AdminEditCompany from './AdminEditCompany';
import { log } from '../../utils/log';

/** A company whose full news search proved coverage-free. */
interface NewsCoverageFlag {
  companyId: number;
  companyName: string | null;
  emptyRunCount: number | null;
  firstFlaggedAt: string | null;
  lastCheckedAt: string | null;
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString();
}

/**
 * Companies flagged as having no news coverage, for admin review. While
 * flagged, a company's scheduled news searches are suppressed apart from a
 * periodic recheck — pinning a concept or clearing the flag resumes them.
 */
const AdminNewsCoverageTable: React.FC = () => {
  const api = useApi();

  const [flags, setFlags] = useState<NewsCoverageFlag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [clearingCompanyId, setClearingCompanyId] = useState<number | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  const fetchFlags = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get('/admin/company/news-coverage-flags');
      const data = response?.data || response;
      setFlags(Array.isArray(data?.results) ? data.results : []);
    } catch (error) {
      log.error('Error fetching news coverage flags:', error);
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchFlags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReview = async (companyId: number): Promise<void> => {
    try {
      const response = await api.get(`/admin/company/${companyId}`);
      const company = response?.data || response;
      if (company?.id) {
        setSelectedCompany(company);
        setEditModalOpen(true);
      }
    } catch (error) {
      log.error('Error loading company for review:', error);
    }
  };

  const handleClearFlag = async (companyId: number): Promise<void> => {
    setClearingCompanyId(companyId);
    try {
      await api.delete(`/admin/company/${companyId}/news-coverage-flag`);
      setFlags((prev) => prev.filter((flag) => flag.companyId !== companyId));
    } catch (error) {
      log.error('Error clearing news coverage flag:', error);
    } finally {
      setClearingCompanyId(null);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        The full news search — concept resolution and every keyword fallback — found nothing for these companies, so
        their scheduled news searches are paused apart from a periodic recheck. If a company should have coverage,
        review it and pin a concept; clearing its flag retries the search from scratch.
      </Typography>

      {flags.length === 0 ? (
        <Typography variant="body1" sx={{ fontStyle: 'italic', p: 2 }}>
          No companies are flagged as coverage-free.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Company</TableCell>
              <TableCell>First flagged</TableCell>
              <TableCell>Last checked</TableCell>
              <TableCell align="right">Empty searches</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {flags.map((flag) => (
              <TableRow key={flag.companyId}>
                <TableCell>
                  {flag.companyName ?? 'Unknown company'}{' '}
                  <Typography component="span" variant="caption" color="text.secondary">
                    (#{flag.companyId})
                  </Typography>
                </TableCell>
                <TableCell>{formatDate(flag.firstFlaggedAt)}</TableCell>
                <TableCell>{formatDate(flag.lastCheckedAt)}</TableCell>
                <TableCell align="right">{flag.emptyRunCount ?? '—'}</TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => handleReview(flag.companyId)}>
                    Review
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleClearFlag(flag.companyId)}
                    disabled={clearingCompanyId === flag.companyId}
                  >
                    {clearingCompanyId === flag.companyId ? <CircularProgress size={16} /> : 'Clear flag'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AdminEditCompany
        isOpen={editModalOpen}
        setIsOpen={() => {
          setEditModalOpen(false);
          setSelectedCompany(null);
          // A pin or flag change inside the modal may have unflagged the company.
          fetchFlags();
        }}
        company={selectedCompany}
        onSave={() => fetchFlags()}
      />
    </Box>
  );
};

export default AdminNewsCoverageTable;
